import { useState, useCallback, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { jsPDF } from "jspdf";
import { 
    Globe, 
    Zap, 
    Smartphone, 
    Search, 
    MousePointerClick,
    Download,
    Phone,
    Mail,
    User,
    MessageSquare,
    AlertTriangle,
    CheckCircle,
    ArrowRight,
    Loader2,
    ExternalLink,
    BarChart3,
    TrendingUp,
    TrendingDown,
    Users,
    FileText,
    Lock,
    Sparkles,
    LogIn,
    Facebook,
    Instagram,
    Tag,
    Clock,
    Bell,
    X,
    Calculator,
    ShoppingBag,
    Menu
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import AdminDashboard from "./AdminDashboard";
import UserDashboard from "./UserDashboard";
import ProfitLeakCalculator from "./ProfitLeakCalculator";
import ReferralProgramBuilder from "./ReferralProgramBuilder";
import ShopifyOptimizationScore from "./ShopifyOptimizationScore";
import PricingPage from "./PricingPage";
import { AuthProvider, useAuth } from "./AuthContext";
import { FeaturesPage, HowItWorksPage, AboutPage, ContactPage, BlogPage, BlogPostWebsiteGrade, SampleReportPage, SEOToolsPage } from "./pages";
import SEO, { SEO_CONFIG } from "./components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_design-scorecard/artifacts/dmpk6oxa_site%20grade%20pro%20logo%20a.png";

// Check if admin mode
const isAdminMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('admin') === 'kamelda2007';
};

// Score Gauge Component
const ScoreGauge = ({ score, size = 120, label }) => {
    const radius = (size - 16) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    
    const getColor = (score) => {
        if (score >= 80) return "#10B981";
        if (score >= 60) return "#F59E0B";
        return "#EF4444";
    };

    return (
        <div className="score-gauge" style={{ width: size, height: size }}>
            <svg width={size} height={size}>
                <circle
                    className="score-gauge-circle"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                />
                <circle
                    className="score-gauge-progress"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={getColor(score)}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{ animation: 'scoreReveal 1.5s ease-out forwards' }}
                />
            </svg>
            <div className="score-gauge-text">
                <span className="text-3xl font-bold font-heading" style={{ color: getColor(score) }}>
                    {score}
                </span>
                {label && <span className="text-xs text-gray-500 block">{label}</span>}
            </div>
        </div>
    );
};

// Category Icon mapping
const getCategoryIcon = (name) => {
    const icons = {
        "Page Speed": Zap,
        "Mobile Responsiveness": Smartphone,
        "SEO Basics": Search,
        "CTA Clarity": MousePointerClick,
    };
    return icons[name] || Globe;
};

// Grade Badge Component
const GradeBadge = ({ grade }) => {
    const getGradeClass = (grade) => {
        if (grade === "A+" || grade === "A") return "grade-a-plus";
        if (grade === "B") return "grade-b";
        if (grade === "C") return "grade-c";
        return "grade-d";
    };

    return (
        <span className={`px-3 py-1 rounded-sm text-sm font-bold font-mono ${getGradeClass(grade)}`}>
            {grade}
        </span>
    );
};

// Category Card Component
const CategoryCard = ({ category, index }) => {
    const Icon = getCategoryIcon(category.name);
    
    return (
        <Card 
            className={`category-card glass-card border-white/10 animate-slide-up`}
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`category-card-${category.name.toLowerCase().replace(/\s/g, '-')}`}
        >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-magenta/10">
                        <Icon className="h-5 w-5 text-magenta" />
                    </div>
                    <CardTitle className="text-lg font-heading">{category.name}</CardTitle>
                </div>
                <GradeBadge grade={category.grade} />
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                    <Progress value={category.score} className="flex-1 h-2" />
                    <span className="text-xl font-bold font-mono text-white">{category.score}</span>
                </div>
                
                {category.issues.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Issues Found</h4>
                        <ul className="space-y-1">
                            {category.issues.map((issue, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                                    {issue}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {category.recommendations.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Recommendations</h4>
                        <ul className="space-y-1">
                            {category.recommendations.map((rec, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                                    {rec}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

// Subscription tier display names and colors
const TIER_CONFIG = {
    free: { name: "Free", color: "gray", scans: 3 },
    starter: { name: "Starter", color: "emerald", scans: 10 },
    pro: { name: "Pro", color: "blue", scans: "Unlimited" },
    premium: { name: "Premium", color: "purple", scans: "Unlimited" }
};

// Main App Component - Inner
function AppContent() {
    const { user, loading, login, loginWithEmail, registerWithEmail, logout, isAuthenticated, refreshUser } = useAuth();
    const [url, setUrl] = useState("");
    const [email, setEmail] = useState("");
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [showConsultation, setShowConsultation] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showAdmin, setShowAdmin] = useState(isAdminMode());
    const [showUserDashboard, setShowUserDashboard] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [previousScore, setPreviousScore] = useState(null);
    const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [showProfitCalculator, setShowProfitCalculator] = useState(false);
    const [showReferralBuilder, setShowReferralBuilder] = useState(false);
    const [showShopifyScore, setShowShopifyScore] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    
    // Page navigation state
    const [currentPage, setCurrentPage] = useState('home');
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    
    // Auth modal state
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
    const [authForm, setAuthForm] = useState({ name: '', email: '', password: '' });
    const [authError, setAuthError] = useState('');
    const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);
    
    // Consultation form state
    const [consultForm, setConsultForm] = useState({
        name: "",
        email: "",
        phone: "",
        website: "",
        message: ""
    });
    const [isSubmittingConsult, setIsSubmittingConsult] = useState(false);
    
    // Fetch subscription data for logged-in users
    useEffect(() => {
        const fetchSubscription = async () => {
            if (isAuthenticated) {
                try {
                    const response = await axios.get(`${API}/user/subscription`, { withCredentials: true });
                    setSubscription(response.data);
                } catch (error) {
                    console.error("Failed to fetch subscription:", error);
                }
            }
        };
        fetchSubscription();
    }, [isAuthenticated]);
    
    // Refresh subscription after payment success
    useEffect(() => {
        const refreshSubscription = async () => {
            if (paymentSuccess && isAuthenticated) {
                try {
                    const response = await axios.get(`${API}/user/subscription`, { withCredentials: true });
                    setSubscription(response.data);
                } catch (error) {
                    console.error("Failed to refresh subscription:", error);
                }
            }
        };
        refreshSubscription();
    }, [paymentSuccess, isAuthenticated]);

    // Check for payment success on page load
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const payment = urlParams.get('payment');
        const sessionId = urlParams.get('session_id');
        
        if (payment === 'success' && sessionId) {
            setIsProcessingPayment(true);
            pollPaymentStatus(sessionId);
        } else if (payment === 'cancelled') {
            toast.error("Payment was cancelled. You can try again anytime.");
            // Clean up URL
            window.history.replaceState({}, '', window.location.pathname);
        }
        
        // Handle direct links to privacy/terms
        if (urlParams.has('privacy')) {
            setShowPrivacyPolicy(true);
        }
        if (urlParams.has('terms')) {
            setShowTerms(true);
        }
        // Handle direct link to profit calculator
        if (urlParams.has('profit-calculator') || urlParams.get('tool') === 'profit-calculator') {
            setShowProfitCalculator(true);
        }
        // Handle direct link to referral builder
        if (urlParams.has('referral-builder') || urlParams.get('tool') === 'referral-builder') {
            setShowReferralBuilder(true);
        }
        // Handle direct link to shopify score
        if (urlParams.has('shopify-score') || urlParams.get('tool') === 'shopify-score') {
            setShowShopifyScore(true);
        }
        // Handle direct link to pricing page
        if (urlParams.has('pricing') || urlParams.get('page') === 'pricing') {
            setShowPricing(true);
        }
        // Handle direct link to dashboard
        if (urlParams.has('dashboard') || urlParams.get('page') === 'dashboard') {
            setShowUserDashboard(true);
        }
        
        // Handle page routes from URL path
        const path = window.location.pathname;
        if (path === '/features') setCurrentPage('features');
        else if (path === '/how-it-works') setCurrentPage('how-it-works');
        else if (path === '/pricing') { setCurrentPage('pricing'); setShowPricing(true); }
        else if (path === '/about') setCurrentPage('about');
        else if (path === '/contact') setCurrentPage('contact');
        else if (path === '/blog/what-website-grade-means') setCurrentPage('blog-post-website-grade');
        else if (path === '/blog') setCurrentPage('blog');
        else if (path === '/sample-report') setCurrentPage('sample-report');
        else if (path === '/seo-tools') setCurrentPage('seo-tools');
        else if (path === '/dashboard') setShowUserDashboard(true);
    }, []);

    // Refresh user on payment success
    useEffect(() => {
        if (paymentSuccess && isAuthenticated) {
            refreshUser();
        }
    }, [paymentSuccess, isAuthenticated, refreshUser]);

    // Poll payment status
    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 10;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            setIsProcessingPayment(false);
            toast.error("Payment status check timed out. Please check your email for confirmation.");
            window.history.replaceState({}, '', window.location.pathname);
            return;
        }

        try {
            const response = await axios.get(`${API}/checkout/status/${sessionId}`);
            const data = response.data;

            if (data.payment_status === 'paid') {
                setIsProcessingPayment(false);
                setPaymentSuccess(true);
                setSelectedPlan(data.plan_name);
                toast.success(`Welcome to SiteGradePro ${data.plan_name}! Payment successful.`);
                window.history.replaceState({}, '', window.location.pathname);
                return;
            } else if (data.status === 'expired') {
                setIsProcessingPayment(false);
                toast.error("Payment session expired. Please try again.");
                window.history.replaceState({}, '', window.location.pathname);
                return;
            }

            // Continue polling
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            console.error("Error checking payment status:", error);
            if (attempts < maxAttempts - 1) {
                setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
            } else {
                setIsProcessingPayment(false);
                toast.error("Error checking payment status.");
                window.history.replaceState({}, '', window.location.pathname);
            }
        }
    };

    // Handle plan checkout
    const handleCheckout = async (planId) => {
        try {
            const response = await axios.post(`${API}/checkout`, {
                plan_id: planId,
                origin_url: window.location.origin,
                email: email || consultForm.email || null
            });
            
            // Redirect to Stripe checkout
            window.location.href = response.data.url;
        } catch (error) {
            console.error("Checkout failed:", error);
            toast.error(error.response?.data?.detail || "Failed to start checkout. Please try again.");
        }
    };

    const analyzeWebsite = useCallback(async () => {
        if (!url.trim()) {
            toast.error("Please enter a website URL");
            return;
        }
        
        // Check scan limits for authenticated users
        if (isAuthenticated && subscription) {
            if (subscription.scans_remaining <= 0) {
                setShowUpgradeModal(true);
                return;
            }
            // Warn when running low on scans
            if (subscription.scans_remaining === 1) {
                toast.warning("This is your last scan this month!");
            }
        }

        setIsAnalyzing(true);
        setAnalysisResult(null);
        setPreviousScore(null);

        try {
            // Fetch previous score for this URL if authenticated
            if (isAuthenticated && subscription?.tier !== 'free') {
                try {
                    const trendsRes = await axios.get(`${API}/user/score-trends`, { withCredentials: true });
                    const normalizedUrl = url.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
                    const existingTrend = trendsRes.data.trends?.find(t => 
                        t.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '') === normalizedUrl
                    );
                    if (existingTrend && existingTrend.scans_count > 0) {
                        setPreviousScore({
                            score: existingTrend.latest_score,
                            date: existingTrend.latest_date
                        });
                    }
                } catch (e) {
                    console.error("Failed to fetch previous score:", e);
                }
            }
            
            const response = await axios.post(`${API}/analyze`, {
                url: url.trim(),
                email: email.trim() || null
            }, { withCredentials: true });
            
            setAnalysisResult(response.data);
            toast.success("Analysis complete!");
            
            // Refresh subscription to update remaining scans
            if (isAuthenticated) {
                try {
                    const subResponse = await axios.get(`${API}/user/subscription`, { withCredentials: true });
                    setSubscription(subResponse.data);
                } catch (e) {
                    console.error("Failed to refresh subscription:", e);
                }
            }
        } catch (error) {
            console.error("Analysis failed:", error);
            // Check if it's a scan limit error
            if (error.response?.status === 403) {
                setShowUpgradeModal(true);
            } else {
                toast.error(error.response?.data?.detail || "Failed to analyze website. Please try again.");
            }
        } finally {
            setIsAnalyzing(false);
        }
    }, [url, email, isAuthenticated, subscription]);

    const generatePDF = useCallback(() => {
        if (!analysisResult) return;
        
        // Check if user has Premium for white-label
        const isPremium = subscription?.tier === 'premium';

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Header
        doc.setFillColor(5, 5, 5);
        doc.rect(0, 0, pageWidth, 50, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.text("PRESSURE REPORT", 20, 25);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        // White-label: Remove SiteGradePro branding for Premium users
        if (!isPremium) {
            doc.text("Website Scorecard by SiteGradePro", 20, 35);
        } else {
            doc.text("Website Performance Analysis", 20, 35);
        }
        
        // Website URL
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(10);
        doc.text(`Analyzed: ${analysisResult.url}`, 20, 45);
        
        // Overall Score Section
        let yPos = 65;
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text("OVERALL SCORE", 20, yPos);
        
        yPos += 15;
        const scoreColor = analysisResult.overall_score >= 80 ? [16, 185, 129] : 
                          analysisResult.overall_score >= 60 ? [245, 158, 11] : [239, 68, 68];
        doc.setTextColor(...scoreColor);
        doc.setFontSize(48);
        doc.text(`${analysisResult.overall_score}`, 20, yPos + 15);
        
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(14);
        doc.text(`Grade: ${analysisResult.overall_grade}`, 70, yPos + 8);
        
        // Summary
        yPos += 40;
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        const summaryLines = doc.splitTextToSize(analysisResult.summary, pageWidth - 40);
        doc.text(summaryLines, 20, yPos);
        yPos += summaryLines.length * 7 + 15;
        
        // Pressure Points
        if (analysisResult.pressure_points.length > 0) {
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(239, 68, 68);
            doc.text("CRITICAL PRESSURE POINTS", 20, yPos);
            yPos += 10;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(60, 60, 60);
            
            analysisResult.pressure_points.forEach((point, i) => {
                const lines = doc.splitTextToSize(`• ${point}`, pageWidth - 45);
                doc.text(lines, 25, yPos);
                yPos += lines.length * 6 + 4;
            });
            yPos += 10;
        }
        
        // Category Scores
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text("DETAILED BREAKDOWN", 20, yPos);
        yPos += 12;
        
        analysisResult.categories.forEach((cat) => {
            if (yPos > 250) {
                doc.addPage();
                yPos = 20;
            }
            
            const catColor = cat.score >= 80 ? [16, 185, 129] : 
                            cat.score >= 60 ? [245, 158, 11] : [239, 68, 68];
            
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(cat.name, 20, yPos);
            
            doc.setTextColor(...catColor);
            doc.text(`${cat.score}/100 (${cat.grade})`, pageWidth - 50, yPos);
            yPos += 8;
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            
            if (cat.issues.length > 0) {
                cat.issues.forEach(issue => {
                    const lines = doc.splitTextToSize(`- ${issue}`, pageWidth - 50);
                    doc.text(lines, 25, yPos);
                    yPos += lines.length * 5 + 2;
                });
            }
            yPos += 8;
        });
        
        // Footer CTA - White-label: different footer for Premium users
        const footerY = doc.internal.pageSize.getHeight() - 30;
        doc.setFillColor(217, 70, 239);
        doc.rect(0, footerY - 5, pageWidth, 35, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        
        if (isPremium) {
            // White-label footer for Premium users
            doc.text("Analysis Complete", 20, footerY + 8);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            doc.text("Generated " + new Date().toLocaleDateString(), 20, footerY + 18);
        } else {
            doc.text("Ready to fix these issues?", 20, footerY + 8);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'normal');
            doc.text("Get expert help from SiteGradePro today!", 20, footerY + 18);
        }

        doc.save(`pressure-report-${analysisResult.url.replace(/[^a-z0-9]/gi, '-')}.pdf`);
        toast.success("PDF report downloaded!");
    }, [analysisResult, subscription]);

    const submitConsultation = useCallback(async (e) => {
        e.preventDefault();
        
        if (!consultForm.name.trim() || !consultForm.email.trim()) {
            toast.error("Please fill in your name and email");
            return;
        }

        setIsSubmittingConsult(true);

        try {
            await axios.post(`${API}/leads`, {
                name: consultForm.name,
                email: consultForm.email,
                phone: consultForm.phone || null,
                website: consultForm.website || analysisResult?.url || null,
                message: consultForm.message || null
            });
            
            toast.success("Thank you! We'll be in touch soon.");
            setShowConsultation(false);
            setConsultForm({ name: "", email: "", phone: "", website: "", message: "" });
        } catch (error) {
            console.error("Lead submission failed:", error);
            toast.error("Failed to submit. Please try again.");
        } finally {
            setIsSubmittingConsult(false);
        }
    }, [consultForm, analysisResult]);

    const resetAnalysis = () => {
        setAnalysisResult(null);
        setUrl("");
        setEmail("");
    };

    // Show admin dashboard if in admin mode (after all hooks)
    if (showAdmin) {
        return <AdminDashboard onBack={() => {
            setShowAdmin(false);
            window.history.replaceState({}, '', window.location.pathname);
        }} />;
    }

    // Show user dashboard
    if (showUserDashboard && isAuthenticated) {
        return <UserDashboard 
            onBack={() => setShowUserDashboard(false)} 
            onAnalyze={() => setShowUserDashboard(false)}
        />;
    }

    // Show Profit Leak Calculator
    if (showProfitCalculator) {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <ProfitLeakCalculator 
                    onBack={(action) => {
                        setShowProfitCalculator(false);
                        window.history.replaceState({}, '', window.location.pathname);
                        if (action === 'consultation') {
                            setShowConsultation(true);
                        }
                    }}
                    user={user}
                    subscription={subscription}
                />
            </>
        );
    }

    // Show Referral Program Builder
    if (showReferralBuilder) {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <ReferralProgramBuilder 
                    onBack={(action) => {
                        setShowReferralBuilder(false);
                        window.history.replaceState({}, '', window.location.pathname);
                        if (action === 'consultation') {
                            setShowConsultation(true);
                        }
                    }}
                    user={user}
                    subscription={subscription}
                />
            </>
        );
    }

    // Show Shopify Optimization Score
    if (showShopifyScore) {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <ShopifyOptimizationScore 
                    onBack={(action) => {
                        setShowShopifyScore(false);
                        window.history.replaceState({}, '', window.location.pathname);
                        if (action === 'consultation') {
                            setShowConsultation(true);
                        }
                    }}
                    user={user}
                    subscription={subscription}
                />
            </>
        );
    }

    // Show Pricing Page
    if (showPricing || currentPage === 'pricing') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <PricingPage 
                    onBack={() => {
                        setShowPricing(false);
                        setCurrentPage('home');
                        window.history.replaceState({}, '', '/');
                    }}
                    onSelectPlan={(action) => {
                        if (action === 'consultation') {
                            setShowPricing(false);
                            setCurrentPage('home');
                            window.history.replaceState({}, '', '/');
                            setShowConsultation(true);
                        }
                    }}
                    onNavigate={(page) => {
                        setShowPricing(false);
                        setCurrentPage(page);
                        window.history.pushState({}, '', `/${page === 'home' ? '' : page}`);
                    }}
                />
            </>
        );
    }

    // Navigation handler for all pages
    const PAGE_URL_MAP = {
        'blog-post-website-grade': '/blog/what-website-grade-means'
    };
    
    const handleNavigate = (page) => {
        setCurrentPage(page);
        setShowPricing(false);
        setShowMobileMenu(false);
        const url = PAGE_URL_MAP[page] || `/${page === 'home' ? '' : page}`;
        window.history.pushState({}, '', url);
        window.scrollTo(0, 0);
    };

    // Handle "Run Audit" from other pages
    const handleRunAudit = () => {
        setCurrentPage('home');
        window.history.pushState({}, '', '/');
        window.scrollTo(0, 0);
        // Focus on URL input after navigation
        setTimeout(() => {
            const urlInput = document.querySelector('[data-testid="url-input"]');
            if (urlInput) urlInput.focus();
        }, 100);
    };

    // Show Features Page
    if (currentPage === 'features') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <FeaturesPage onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show How It Works Page
    if (currentPage === 'how-it-works') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <HowItWorksPage onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show About Page
    if (currentPage === 'about') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <AboutPage onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show Contact Page
    if (currentPage === 'contact') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <ContactPage onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show Blog Post: What a Website Grade Really Means
    if (currentPage === 'blog-post-website-grade') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <BlogPostWebsiteGrade onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show Blog Page
    if (currentPage === 'blog') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <BlogPage onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show Sample Report Page
    if (currentPage === 'sample-report') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <SampleReportPage onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show SEO Tools Page
    if (currentPage === 'seo-tools') {
        return (
            <>
                <Toaster position="top-right" theme="dark" />
                <SEOToolsPage onRunAudit={handleRunAudit} onNavigate={handleNavigate} />
            </>
        );
    }

    // Show loading while checking auth
    if (loading) {
        return (
            <div className="min-h-screen bg-obsidian flex items-center justify-center">
                <div className="loading-spinner" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-obsidian">
            <SEO 
                title={SEO_CONFIG.home.title} 
                description={SEO_CONFIG.home.description} 
                keywords={SEO_CONFIG.home.keywords} 
            />
            <Toaster position="top-right" theme="dark" />
            <div className="noise-overlay" />
            
            {/* Payment Processing Modal */}
            {isProcessingPayment && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90">
                    <div className="text-center">
                        <div className="loading-spinner mx-auto mb-4" />
                        <h2 className="text-2xl font-heading font-bold text-white mb-2">Processing Payment...</h2>
                        <p className="text-gray-400">Please wait while we confirm your subscription.</p>
                    </div>
                </div>
            )}

            {/* Payment Success Banner */}
            {paymentSuccess && (
                <div className="fixed top-0 left-0 right-0 z-[60] bg-emerald-500 text-white py-3 px-4 text-center">
                    <p className="font-bold">
                        <CheckCircle className="inline h-5 w-5 mr-2" />
                        Welcome to SiteGradePro {selectedPlan}! Check your email for account setup details.
                        <button 
                            onClick={() => setPaymentSuccess(false)} 
                            className="ml-4 underline hover:no-underline"
                        >
                            Dismiss
                        </button>
                    </p>
                </div>
            )}
            
            {/* Header */}
            <header className={`fixed ${paymentSuccess ? 'top-12' : 'top-0'} left-0 right-0 z-50 glass-card border-b border-white/5 transition-all`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-8">
                        <button onClick={() => handleNavigate('home')} className="flex items-center">
                            <img 
                                src={LOGO_URL} 
                                alt="SiteGradePro" 
                                className="h-12 sm:h-14 lg:h-16 w-auto object-contain"
                                data-testid="logo"
                            />
                        </button>
                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center gap-6">
                            <button onClick={() => handleNavigate('features')} className="text-sm text-gray-400 hover:text-white transition-colors">Features</button>
                            <button onClick={() => handleNavigate('how-it-works')} className="text-sm text-gray-400 hover:text-white transition-colors">How It Works</button>
                            <button onClick={() => { setShowPricing(true); setCurrentPage('pricing'); window.history.pushState({}, '', '/pricing'); }} className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</button>
                            <button onClick={() => handleNavigate('about')} className="text-sm text-gray-400 hover:text-white transition-colors">About</button>
                            <button onClick={() => handleNavigate('contact')} className="text-sm text-gray-400 hover:text-white transition-colors">Contact</button>
                            <button onClick={() => handleNavigate('blog')} className="text-sm text-gray-400 hover:text-white transition-colors">Blog</button>
                        </nav>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Mobile Menu Button */}
                        <button 
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                            className="lg:hidden p-2 text-gray-400 hover:text-white"
                            data-testid="mobile-menu-btn"
                        >
                            <Menu className="h-6 w-6" />
                        </button>
                        
                        {isAuthenticated ? (
                            <>
                                {/* Subscription Badge with Scans */}
                                {subscription && (
                                    <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10" data-testid="subscription-badge">
                                        <span className={`text-xs font-bold uppercase text-${TIER_CONFIG[subscription.tier]?.color || 'gray'}-400`}>
                                            {TIER_CONFIG[subscription.tier]?.name || subscription.tier}
                                        </span>
                                        <span className="text-gray-500">|</span>
                                        <span className="text-xs text-gray-400">
                                            <Zap className="h-3 w-3 inline mr-1" />
                                            {subscription.scans_remaining === 999999 ? '∞' : subscription.scans_remaining} scans
                                        </span>
                                    </div>
                                )}
                                <Button 
                                    onClick={() => setShowUserDashboard(true)}
                                    variant="outline"
                                    className="hidden sm:flex border-gray-700 text-gray-300 hover:border-white hover:text-white"
                                    data-testid="dashboard-btn"
                                >
                                    <User className="h-4 w-4 mr-2" />
                                    Dashboard
                                </Button>
                                <Button 
                                    onClick={() => setShowConsultation(true)}
                                    className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-4 sm:px-6 py-2 text-sm btn-primary"
                                    data-testid="header-cta-btn"
                                >
                                    <span className="hidden sm:inline">Get Expert Help</span>
                                    <span className="sm:hidden">Help</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button 
                                    onClick={() => setShowAuthModal(true)}
                                    variant="outline"
                                    className="hidden sm:flex border-gray-700 text-gray-300 hover:border-white hover:text-white"
                                    data-testid="login-btn"
                                >
                                    <LogIn className="h-4 w-4 mr-2" />
                                    Sign In
                                </Button>
                                <Button 
                                    onClick={() => setShowConsultation(true)}
                                    className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-4 sm:px-6 py-2 text-sm btn-primary"
                                    data-testid="header-cta-btn"
                                >
                                    <span className="hidden sm:inline">Get Expert Help</span>
                                    <span className="sm:hidden">Help</span>
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                
                {/* Mobile Menu */}
                {showMobileMenu && (
                    <div className="lg:hidden border-t border-white/5 bg-black/95 backdrop-blur-lg">
                        <nav className="flex flex-col p-4 space-y-3">
                            <button onClick={() => handleNavigate('features')} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">Features</button>
                            <button onClick={() => handleNavigate('how-it-works')} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">How It Works</button>
                            <button onClick={() => { setShowPricing(true); setCurrentPage('pricing'); setShowMobileMenu(false); }} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">Pricing</button>
                            <button onClick={() => handleNavigate('about')} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">About</button>
                            <button onClick={() => handleNavigate('contact')} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">Contact</button>
                            <button onClick={() => handleNavigate('blog')} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">Blog</button>
                            <div className="border-t border-white/10 pt-3 mt-3">
                                {isAuthenticated ? (
                                    <button onClick={() => { setShowUserDashboard(true); setShowMobileMenu(false); }} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors w-full">Dashboard</button>
                                ) : (
                                    <button onClick={() => { setShowAuthModal(true); setShowMobileMenu(false); }} className="text-left text-gray-300 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors w-full">Sign In</button>
                                )}
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            {/* Main Content */}
            <main className={`${paymentSuccess ? 'pt-36' : 'pt-24'} transition-all`}>
                {!analysisResult ? (
                    <>
                    {/* Hero Section */}
                    <section className="hero-bg min-h-[calc(100vh-6rem)] flex items-center">
                        <div className="hero-overlay absolute inset-0" />
                        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
                            <span className="inline-block px-4 py-1 bg-magenta/20 text-magenta text-sm font-mono tracking-wider rounded-sm mb-6 animate-slide-up">
                                FREE WEBSITE AUDIT
                            </span>
                            
                            <h1 className="text-5xl md:text-7xl font-bold font-heading tracking-tighter uppercase text-white mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                                Get Your Website Grade <br />
                                <span className="chrome-text">in Seconds</span>
                            </h1>
                            
                            <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto animate-slide-up leading-relaxed" style={{ animationDelay: '0.2s' }}>
                                SiteGradePro instantly analyzes your SEO, speed, mobile optimization, 
                                and user experience — then gives you a clear, actionable report.
                            </p>

                            {/* URL Input Form */}
                            <div className="space-y-4 animate-slide-up max-w-2xl mx-auto" style={{ animationDelay: '0.3s' }}>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1">
                                        <Input
                                            type="text"
                                            placeholder="Enter your website URL (e.g., example.com)"
                                            value={url}
                                            onChange={(e) => setUrl(e.target.value)}
                                            className="url-input hero-input bg-black/50 border-2 border-gray-800 focus:border-magenta text-white text-lg p-6 rounded-md w-full h-14"
                                            data-testid="url-input"
                                            disabled={isAnalyzing}
                                        />
                                    </div>
                                    <Button
                                        onClick={analyzeWebsite}
                                        disabled={isAnalyzing || !url.trim()}
                                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-8 py-3 h-14 text-base btn-primary disabled:opacity-50"
                                        data-testid="analyze-btn"
                                    >
                                        {isAnalyzing ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Analyzing...
                                            </>
                                        ) : (
                                            <>
                                                Run Free Audit
                                                <ArrowRight className="ml-2 h-5 w-5" />
                                            </>
                                        )}
                                    </Button>
                                </div>
                                
                                <div className="flex items-center gap-4">
                                    <Input
                                        type="email"
                                        placeholder="Your email (optional - to receive report)"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="hero-input bg-black/30 border border-gray-800 focus:border-magenta/50 text-white text-sm p-4 rounded-md flex-1"
                                        data-testid="email-input"
                                        disabled={isAnalyzing}
                                    />
                                </div>
                                
                                {/* Secondary CTA */}
                                <div className="pt-2">
                                    <button 
                                        onClick={() => handleNavigate('features')}
                                        className="text-gray-400 hover:text-white text-sm transition-colors"
                                    >
                                        View Features <ArrowRight className="inline h-4 w-4 ml-1" />
                                    </button>
                                </div>
                            </div>

                            {/* Loading State */}
                            {isAnalyzing && (
                                <div className="mt-12 glass-card rounded-xl p-8 text-center animate-scale-in max-w-md mx-auto" data-testid="analyzing-state">
                                    <div className="loading-spinner mx-auto mb-4" />
                                    <p className="text-gray-300 analyzing-pulse">Analyzing your website...</p>
                                    <p className="text-sm text-gray-500 mt-2">This may take 15-30 seconds</p>
                                </div>
                            )}

                            {/* Trust indicators */}
                            <div className="mt-12 flex flex-wrap items-center justify-center gap-4 md:gap-8 text-sm animate-slide-up p-3 md:p-0 rounded-lg bg-black/60 md:bg-transparent" style={{ animationDelay: '0.5s' }}>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    <span className="text-gray-200 md:text-gray-400">100% Free</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    <span className="text-gray-200 md:text-gray-400">No Credit Card</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    <span className="text-gray-200 md:text-gray-400">Instant Results</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Trust Section */}
                    <section className="py-20 px-4 bg-gradient-to-b from-black to-white/5">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Built for Small Businesses & Growing Brands
                            </h2>
                            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                                You don't need a developer to understand what's wrong with your website.
                                SiteGradePro breaks it down in plain English — no technical overwhelm.
                            </p>
                        </div>
                    </section>

                    {/* What We Analyze Section */}
                    <section className="py-20 px-4">
                        <div className="max-w-6xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
                                What We Analyze
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <Card className="bg-white/5 border-white/10 hover:border-cyan-500/30 transition-colors">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                                            <Search className="h-7 w-7 text-cyan-400" />
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-2">SEO Structure</h3>
                                        <p className="text-sm text-gray-400">Meta tags, headers, keywords & on-page optimization</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white/5 border-white/10 hover:border-yellow-500/30 transition-colors">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/20 flex items-center justify-center mx-auto mb-4">
                                            <Zap className="h-7 w-7 text-yellow-400" />
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-2">Performance & Speed</h3>
                                        <p className="text-sm text-gray-400">Page load time, image optimization & script analysis</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white/5 border-white/10 hover:border-green-500/30 transition-colors">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                                            <Smartphone className="h-7 w-7 text-green-400" />
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-2">Mobile Optimization</h3>
                                        <p className="text-sm text-gray-400">Responsiveness, layout & mobile user experience</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white/5 border-white/10 hover:border-magenta/30 transition-colors">
                                    <CardContent className="p-6 text-center">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-magenta/20 flex items-center justify-center mx-auto mb-4">
                                            <MousePointerClick className="h-7 w-7 text-magenta" />
                                        </div>
                                        <h3 className="text-lg font-bold text-white mb-2">Conversion & UX</h3>
                                        <p className="text-sm text-gray-400">CTA clarity, trust signals & visual hierarchy</p>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="text-center mt-10">
                                <Button 
                                    onClick={() => handleNavigate('features')}
                                    variant="outline"
                                    className="border-white/20 text-white hover:bg-white/10"
                                >
                                    See Full Feature Breakdown
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </section>

                    {/* Sample Report Preview Section */}
                    <section className="py-20 px-4 bg-gradient-to-b from-transparent to-white/5">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                Here's What Your Website Score Looks Like
                            </h2>
                            <p className="text-gray-400 mb-10">
                                Get a clear letter grade with category breakdown and priority fixes
                            </p>
                            
                            {/* Sample Score Card */}
                            <Card className="bg-white/5 border-white/10 max-w-md mx-auto mb-10">
                                <CardContent className="p-8">
                                    <div className="text-7xl font-bold text-emerald-400 mb-2">B+</div>
                                    <div className="text-gray-400 mb-6">Sample Website Score</div>
                                    <div className="space-y-3 text-left">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-300">SEO Structure</span>
                                            <span className="text-cyan-400 font-medium">85/100</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-300">Performance</span>
                                            <span className="text-yellow-400 font-medium">72/100</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-300">Mobile</span>
                                            <span className="text-green-400 font-medium">90/100</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-300">Conversion</span>
                                            <span className="text-magenta font-medium">78/100</span>
                                        </div>
                                    </div>
                                    <div className="border-t border-white/10 mt-6 pt-6">
                                        <p className="text-sm text-gray-400 mb-2">Top 3 Priority Fixes:</p>
                                        <ul className="text-sm text-left space-y-1">
                                            <li className="text-gray-300">1. Optimize image sizes</li>
                                            <li className="text-gray-300">2. Add meta descriptions</li>
                                            <li className="text-gray-300">3. Improve CTA visibility</li>
                                        </ul>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Button 
                                onClick={() => {
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                    setTimeout(() => {
                                        const urlInput = document.querySelector('[data-testid="url-input"]');
                                        if (urlInput) urlInput.focus();
                                    }, 500);
                                }}
                                size="lg"
                                className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-6 text-lg"
                            >
                                Get My Website Score
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </div>
                    </section>

                    {/* Pro Report Preview Section */}
                    <section className="py-20 px-4" data-testid="pro-report-preview-section">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-500/20 text-violet-400 text-sm font-medium rounded-full mb-4">
                                    <Sparkles className="h-4 w-4" />
                                    Pro Fix Plan — $19
                                </div>
                                <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                                    See What's Inside Your Pro Report
                                </h2>
                                <p className="text-gray-400 max-w-2xl mx-auto">
                                    10+ pages of personalized fixes, ranked by ROI, with a 30-day action plan. Here's a sneak peek.
                                </p>
                            </div>

                            {/* Blurred Report Pages Preview */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                                {/* Page 1 - Cover */}
                                <div className="bg-white rounded-lg shadow-xl shadow-violet-500/5 overflow-hidden transform hover:scale-[1.02] transition-transform">
                                    <div className="bg-[#0a0a0a] p-4">
                                        <p className="text-violet-400 text-[10px] font-bold tracking-wider">SITE GRADE PRO</p>
                                        <p className="text-white text-base font-bold mt-1">Profit Leak Fix Plan</p>
                                    </div>
                                    <div className="p-4 bg-white">
                                        <div className="bg-gray-50 rounded p-2.5 mb-3">
                                            <p className="text-gray-400 text-[9px] uppercase tracking-wider">Report for</p>
                                            <p className="text-gray-800 font-bold text-xs">Your Business Name</p>
                                            <p className="text-violet-500 text-[10px]">yourwebsite.com</p>
                                        </div>
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-gray-400 text-[9px]">Revenue Score</span>
                                            <div><span className="text-amber-500 font-bold text-lg">30</span><span className="text-gray-400 text-[10px]">/70</span></div>
                                        </div>
                                        <div className="space-y-1">
                                            {["Executive Summary", "Leak Breakdown", "Priority Fixes", "30-Day Plan"].map((s, i) => (
                                                <p key={i} className="text-gray-500 text-[9px]">{i + 1}. {s}</p>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Page 2 - Partially blurred */}
                                <div className="bg-white rounded-lg shadow-xl shadow-violet-500/5 overflow-hidden relative transform hover:scale-[1.02] transition-transform">
                                    <div className="bg-[#0a0a0a] px-4 py-2.5">
                                        <p className="text-white text-[10px] font-bold">EXECUTIVE SUMMARY</p>
                                    </div>
                                    <div className="p-4 bg-white">
                                        <p className="text-gray-700 text-[9px] leading-relaxed mb-2">Based on our analysis, your business scored 30/70 on the Revenue Scale, indicating 4 revenue leaks...</p>
                                        <div className="space-y-1">
                                            {[{ n: "Email Capture", p: true }, { n: "Cart Recovery", p: false }, { n: "Upsells", p: false }, { n: "Social Proof", p: true }, { n: "Page Speed", p: false }].map((c, i) => (
                                                <div key={i} className="flex items-center justify-between bg-gray-50 rounded px-2 py-0.5">
                                                    <span className="text-gray-600 text-[9px]">{c.n}</span>
                                                    <span className={`text-[9px] font-bold ${c.p ? 'text-emerald-500' : 'text-red-500'}`}>{c.p ? 'PASS' : 'LEAK'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-white via-white/95 to-transparent flex items-end justify-center pb-3">
                                        <span className="flex items-center gap-1 text-violet-500 text-[10px] font-medium"><Lock className="h-2.5 w-2.5" /> Pro only</span>
                                    </div>
                                </div>

                                {/* Page 3 - Heavily blurred */}
                                <div className="bg-white rounded-lg shadow-xl shadow-violet-500/5 overflow-hidden relative transform hover:scale-[1.02] transition-transform">
                                    <div className="bg-[#0a0a0a] px-4 py-2.5">
                                        <p className="text-white text-[10px] font-bold">30-DAY ACTION PLAN</p>
                                    </div>
                                    <div className="p-4 bg-white">
                                        <div className="space-y-2">
                                            <div className="bg-gray-50 rounded p-2">
                                                <p className="text-violet-500 text-[9px] font-bold">Week 1: Quick Wins</p>
                                                <p className="text-gray-600 text-[9px]">Set up abandoned cart emails</p>
                                            </div>
                                            <div className="bg-gray-50 rounded p-2 blur-[2px]">
                                                <p className="text-violet-500 text-[9px] font-bold">Week 2: Revenue Recovery</p>
                                                <p className="text-gray-500 text-[9px]">Create product bundles...</p>
                                            </div>
                                            <div className="bg-gray-50 rounded p-2 blur-[3px]">
                                                <p className="text-violet-500 text-[9px] font-bold">Week 3: Optimization</p>
                                                <p className="text-gray-500 text-[9px]">Speed improvements...</p>
                                            </div>
                                            <div className="bg-gray-50 rounded p-2 blur-[3px]">
                                                <p className="text-violet-500 text-[9px] font-bold">Week 4: Scale</p>
                                                <p className="text-gray-500 text-[9px]">Campaign launch...</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 h-2/3 bg-gradient-to-t from-white via-white/90 to-transparent flex items-end justify-center pb-3">
                                        <span className="flex items-center gap-1 text-violet-500 text-[10px] font-medium"><Lock className="h-2.5 w-2.5" /> Unlock for $19</span>
                                    </div>
                                </div>
                            </div>

                            {/* Section list + CTA */}
                            <Card className="bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border-violet-500/30">
                                <CardContent className="p-8 md:p-10">
                                    <div className="flex flex-col md:flex-row gap-8 items-center">
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold text-white mb-4">What You Get for $19</h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                {[
                                                    "Executive Summary & Score",
                                                    "Revenue Leak Analysis",
                                                    "ROI-Ranked Priority Fixes",
                                                    "30-Day Action Plan",
                                                    "Revenue Projections",
                                                    "Tool Recommendations",
                                                    "DIY vs Done-For-You Guide",
                                                    "Instant PDF Download"
                                                ].map((item, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                                                        <span className="text-sm text-gray-300">{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-3">
                                            <Button
                                                onClick={() => handleNavigate('sample-report')}
                                                size="lg"
                                                className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-8"
                                                data-testid="view-sample-report-btn"
                                            >
                                                View Full Sample
                                                <ArrowRight className="ml-2 h-4 w-4" />
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                    setTimeout(() => {
                                                        const urlInput = document.querySelector('[data-testid="url-input"]');
                                                        if (urlInput) urlInput.focus();
                                                    }, 500);
                                                }}
                                                size="lg"
                                                variant="outline"
                                                className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8"
                                            >
                                                Run Free Audit First
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </section>

                    {/* Tools Section */}
                    <section className="py-20 px-4 bg-gradient-to-b from-transparent to-white/5">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                                More Free Tools
                            </h2>
                            <p className="text-gray-400 mb-10">
                                Explore our suite of business optimization calculators
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <button
                                    onClick={() => setShowProfitCalculator(true)}
                                    className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/30 rounded-lg transition-all"
                                    data-testid="profit-calculator-promo"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
                                        <Calculator className="h-6 w-6 text-cyan-400" />
                                    </div>
                                    <div className="text-left flex-1 min-w-0">
                                        <p className="text-white font-medium group-hover:text-cyan-400 transition-colors truncate">
                                            Profit Leak Calculator
                                        </p>
                                        <p className="text-sm text-gray-500 truncate">
                                            Find hidden revenue gaps
                                        </p>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-gray-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all flex-shrink-0" />
                                </button>

                                <button
                                    onClick={() => setShowReferralBuilder(true)}
                                    className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-violet-500/30 rounded-lg transition-all"
                                    data-testid="referral-builder-promo"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/20 flex items-center justify-center flex-shrink-0">
                                        <Users className="h-6 w-6 text-violet-400" />
                                    </div>
                                    <div className="text-left flex-1 min-w-0">
                                        <p className="text-white font-medium group-hover:text-violet-400 transition-colors truncate">
                                            Referral Builder
                                        </p>
                                        <p className="text-sm text-gray-500 truncate">
                                            Design commission structure
                                        </p>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-gray-500 group-hover:text-violet-400 group-hover:translate-x-1 transition-all flex-shrink-0" />
                                </button>

                                <button
                                    onClick={() => setShowShopifyScore(true)}
                                    className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-green-500/30 rounded-lg transition-all"
                                    data-testid="shopify-score-promo"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/20 flex items-center justify-center flex-shrink-0">
                                        <ShoppingBag className="h-6 w-6 text-green-400" />
                                    </div>
                                    <div className="text-left flex-1 min-w-0">
                                        <p className="text-white font-medium group-hover:text-green-400 transition-colors truncate">
                                            Shopify Score
                                        </p>
                                        <p className="text-sm text-gray-500 truncate">
                                            Optimize your store
                                        </p>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-gray-500 group-hover:text-green-400 group-hover:translate-x-1 transition-all flex-shrink-0" />
                                </button>
                            </div>
                        </div>
                    </section>
                    </>
                ) : (
                    /* Results Section */
                    <section className="py-12 px-4 sm:px-6 lg:px-8" data-testid="results-section">
                        <div className="max-w-7xl mx-auto">
                            {/* Results Header */}
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-12">
                                <div>
                                    <h2 className="text-3xl md:text-4xl font-bold font-heading text-white mb-2">
                                        Your Website Scorecard
                                    </h2>
                                    <div className="flex items-center gap-2 text-gray-400">
                                        <Globe className="h-4 w-4" />
                                        <a 
                                            href={analysisResult.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="hover:text-magenta transition-colors flex items-center gap-1"
                                        >
                                            {analysisResult.url}
                                            <ExternalLink className="h-3 w-3" />
                                        </a>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={resetAnalysis}
                                        className="border-gray-700 text-gray-300 hover:border-white hover:text-white"
                                        data-testid="new-analysis-btn"
                                    >
                                        New Analysis
                                    </Button>
                                    <Button
                                        onClick={generatePDF}
                                        className="bg-magenta hover:bg-magenta-dark text-white font-bold"
                                        data-testid="download-pdf-btn"
                                    >
                                        <Download className="mr-2 h-4 w-4" />
                                        Download Report
                                    </Button>
                                </div>
                            </div>

                            {/* Overall Score */}
                            <Card className="glass-card border-white/10 mb-8 animate-slide-up" data-testid="overall-score-card">
                                <CardContent className="p-8">
                                    <div className="flex flex-col md:flex-row items-center gap-8">
                                        <div className="relative">
                                            <ScoreGauge score={analysisResult.overall_score} size={160} />
                                            {/* Score Change Badge */}
                                            {previousScore && (
                                                <div 
                                                    className={`absolute -top-2 -right-2 px-2 py-1 rounded-full text-sm font-bold flex items-center gap-1 ${
                                                        analysisResult.overall_score > previousScore.score 
                                                            ? 'bg-emerald-500 text-white' 
                                                            : analysisResult.overall_score < previousScore.score 
                                                                ? 'bg-red-500 text-white'
                                                                : 'bg-gray-500 text-white'
                                                    }`}
                                                    data-testid="score-change-badge"
                                                >
                                                    {analysisResult.overall_score > previousScore.score ? (
                                                        <TrendingUp className="h-3 w-3" />
                                                    ) : analysisResult.overall_score < previousScore.score ? (
                                                        <TrendingDown className="h-3 w-3" />
                                                    ) : null}
                                                    {analysisResult.overall_score > previousScore.score ? '+' : ''}
                                                    {analysisResult.overall_score - previousScore.score}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 text-center md:text-left">
                                            <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                                                <h3 className="text-2xl font-heading font-bold text-white">Overall Score</h3>
                                                <GradeBadge grade={analysisResult.overall_grade} />
                                            </div>
                                            <p className="text-gray-300 text-lg leading-relaxed">{analysisResult.summary}</p>
                                            
                                            {/* Previous Score Comparison */}
                                            {previousScore && (
                                                <div className="mt-4 p-3 bg-white/5 rounded-lg border border-white/10" data-testid="previous-score-comparison">
                                                    <p className="text-sm text-gray-400">
                                                        {analysisResult.overall_score > previousScore.score ? (
                                                            <span className="text-emerald-400 font-semibold">
                                                                Great progress! Your score improved by {analysisResult.overall_score - previousScore.score} points
                                                            </span>
                                                        ) : analysisResult.overall_score < previousScore.score ? (
                                                            <span className="text-amber-400 font-semibold">
                                                                Score dropped by {previousScore.score - analysisResult.overall_score} points since last scan
                                                            </span>
                                                        ) : (
                                                            <span className="text-gray-300">
                                                                Same score as your previous scan
                                                            </span>
                                                        )}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        Previous: {previousScore.score} points
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Pressure Points */}
                            {analysisResult.pressure_points.length > 0 && (
                                <Card className="glass-card border-red-500/20 mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }} data-testid="pressure-points-card">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-red-400 font-heading">
                                            <AlertTriangle className="h-5 w-5" />
                                            Critical Pressure Points
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ul className="space-y-3">
                                            {analysisResult.pressure_points.map((point, i) => (
                                                <li key={i} className="pressure-point text-gray-300">{point}</li>
                                            ))}
                                        </ul>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Category Cards Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                                {analysisResult.categories.map((category, index) => (
                                    <CategoryCard key={category.name} category={category} index={index} />
                                ))}
                            </div>

                            {/* CTA Section */}
                            <Card className="chrome-border glass-card border-magenta/30 animate-slide-up" style={{ animationDelay: '0.4s' }} data-testid="cta-card">
                                <CardContent className="p-8 md:p-12 text-center">
                                    <h3 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                                        Ready to Fix These Issues?
                                    </h3>
                                    <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                                        Get expert help from our design team and transform your website 
                                        into a high-performing, lead-generating machine.
                                    </p>
                                    <Button
                                        onClick={() => setShowConsultation(true)}
                                        size="lg"
                                        className="bg-white text-black font-bold uppercase tracking-widest hover:bg-gray-200 rounded-sm px-10 py-4 text-lg btn-primary"
                                        data-testid="book-consultation-btn"
                                    >
                                        Get Expert Help
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Button>
                                </CardContent>
                            </Card>

                            {/* Discord Community Banner */}
                            <div className="mt-8 animate-slide-up" style={{ animationDelay: '0.45s' }}>
                                <a 
                                    href="https://discord.gg/GAfmKjNk" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="block"
                                    data-testid="discord-community-link"
                                >
                                    <Card className="glass-card border-[#5865F2]/30 hover:border-[#5865F2]/60 transition-all group cursor-pointer">
                                        <CardContent className="p-6 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-xl bg-[#5865F2]/20 flex items-center justify-center group-hover:bg-[#5865F2]/30 transition-colors">
                                                    <svg className="h-6 w-6 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h4 className="text-white font-semibold group-hover:text-[#5865F2] transition-colors">Join Our Discord Community</h4>
                                                    <p className="text-gray-400 text-sm">Get tips, share results & connect with other site owners</p>
                                                </div>
                                            </div>
                                            <ArrowRight className="h-5 w-5 text-gray-500 group-hover:text-[#5865F2] group-hover:translate-x-1 transition-all" />
                                        </CardContent>
                                    </Card>
                                </a>
                            </div>

                            {/* Premium Upsell Section */}
                            <div className="mt-16 animate-slide-up" style={{ animationDelay: '0.5s' }} data-testid="premium-upsell-section">
                                <div className="text-center mb-10">
                                    {/* Launch Offer Banner */}
                                    <div className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold tracking-wider rounded-full mb-6 animate-pulse shadow-lg shadow-amber-500/30" data-testid="launch-offer-badge">
                                        <Tag className="h-4 w-4" />
                                        LAUNCH LIMITED TIME OFFER - 20% OFF!
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                                        Unlock Deeper Insights
                                    </h3>
                                    <p className="text-gray-400 max-w-2xl mx-auto">
                                        Choose a plan to monitor your website's performance, track improvements, and generate more leads.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                                    {/* Starter Tier */}
                                    <Card className="glass-card border-white/10 relative overflow-hidden group hover:border-emerald-500/30 transition-colors" data-testid="pricing-starter">
                                        {/* Discount Badge */}
                                        <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500 text-black text-xs font-bold rounded">
                                            SAVE 20%
                                        </div>
                                        <CardContent className="p-6">
                                            <div className="mb-4">
                                                <h4 className="text-lg font-heading font-bold text-emerald-400 mb-1">Starter</h4>
                                                <p className="text-gray-500 text-sm">For solopreneurs & small sites</p>
                                            </div>
                                            <div className="mb-6">
                                                <span className="text-xl text-gray-500 line-through mr-2">$19</span>
                                                <span className="text-4xl font-bold bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent">$15</span>
                                                <span className="text-gray-500">/month</span>
                                            </div>
                                            <ul className="space-y-3 text-sm mb-6">
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                                                    Basic website scoring
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                                                    10 monthly scans
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                                                    Basic PDF report
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                                                    30-day score history
                                                </li>
                                            </ul>
                                            <Button
                                                onClick={() => handleCheckout('starter')}
                                                variant="outline"
                                                className="w-full border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-400"
                                                data-testid="starter-cta-btn"
                                            >
                                                Get Started
                                            </Button>
                                        </CardContent>
                                    </Card>

                                    {/* Pro Tier - Most Popular */}
                                    <Card className="glass-card border-blue-500/50 relative overflow-hidden group hover:border-blue-400 transition-colors scale-105 shadow-[0_0_30px_rgba(59,130,246,0.2)]" data-testid="pricing-pro">
                                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-center py-1 text-xs font-bold uppercase tracking-wider">
                                            Most Popular - Save 20%
                                        </div>
                                        <CardContent className="p-6 pt-10">
                                            <div className="mb-4">
                                                <h4 className="text-lg font-heading font-bold text-blue-400 mb-1">Pro</h4>
                                                <p className="text-gray-500 text-sm">For growing businesses</p>
                                            </div>
                                            <div className="mb-6">
                                                <span className="text-xl text-gray-500 line-through mr-2">$39</span>
                                                <span className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">$31</span>
                                                <span className="text-gray-500">/month</span>
                                            </div>
                                            <ul className="space-y-3 text-sm mb-6">
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                                                    <strong className="text-white">Unlimited</strong>&nbsp;scans
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                                                    Analytics dashboard
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                                                    Score history charts
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                                                    Lead conversion tracking
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                                                    Scheduled re-scans
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                                                    90-day history
                                                </li>
                                            </ul>
                                            <Button
                                                onClick={() => handleCheckout('pro')}
                                                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold"
                                                data-testid="pro-cta-btn"
                                            >
                                                Get Pro
                                            </Button>
                                        </CardContent>
                                    </Card>

                                    {/* Premium Tier */}
                                    <Card className="glass-card border-white/10 relative overflow-hidden group hover:border-purple-500/30 transition-colors" data-testid="pricing-premium">
                                        {/* Discount Badge */}
                                        <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500 text-black text-xs font-bold rounded">
                                            SAVE 20%
                                        </div>
                                        <CardContent className="p-6">
                                            <div className="mb-4">
                                                <h4 className="text-lg font-heading font-bold text-purple-400 mb-1">Premium</h4>
                                                <p className="text-gray-500 text-sm">For agencies & serious operators</p>
                                            </div>
                                            <div className="mb-6">
                                                <span className="text-xl text-gray-500 line-through mr-2">$79</span>
                                                <span className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">$63</span>
                                                <span className="text-gray-500">/month</span>
                                            </div>
                                            <ul className="space-y-3 text-sm mb-6">
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                                                    Everything in Pro
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                                                    Email score alerts
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                                                    Full audit trail
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                                                    Competitor benchmarking
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                                                    Exportable reports
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                                                    White-label PDF reports
                                                </li>
                                                <li className="flex items-center gap-2 text-gray-300">
                                                    <CheckCircle className="h-4 w-4 text-purple-500 shrink-0" />
                                                    1-year score history
                                                </li>
                                            </ul>
                                            <Button
                                                onClick={() => handleCheckout('premium')}
                                                variant="outline"
                                                className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:border-purple-400"
                                                data-testid="premium-cta-btn"
                                            >
                                                Go Premium
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </div>

                                <p className="text-center text-gray-500 text-sm mt-8">
                                    All plans include email support. Questions? <button onClick={() => setShowConsultation(true)} className="text-magenta hover:underline">Contact us</button>
                                </p>
                            </div>
                        </div>
                    </section>
                )}
            </main>

            {/* Footer */}
            <footer className="border-t border-white/5 py-8 mt-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <img src={LOGO_URL} alt="SiteGradePro" className="h-10 w-auto object-contain" />
                        <span className="text-gray-400 text-sm">© 2026 SiteGradePro. All rights reserved.</span>
                    </div>
                    
                    {/* Social Media Icons */}
                    <div className="flex items-center gap-4" data-testid="social-media-links">
                        <a 
                            href="https://facebook.com/sitegradepro" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-magenta transition-colors"
                            data-testid="social-facebook"
                            aria-label="Facebook"
                        >
                            <Facebook className="h-5 w-5" />
                        </a>
                        <a 
                            href="https://instagram.com/sitegradepro" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-magenta transition-colors"
                            data-testid="social-instagram"
                            aria-label="Instagram"
                        >
                            <Instagram className="h-5 w-5" />
                        </a>
                        <a 
                            href="https://threads.net/@sitegradepro" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-magenta transition-colors"
                            data-testid="social-threads"
                            aria-label="Threads"
                        >
                            {/* Threads icon (custom SVG) */}
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.5 12.068V12c.008-3.57.858-6.432 2.528-8.503C5.857 1.205 8.61.024 12.19 0h.014c2.746.017 5.063.768 6.886 2.231 1.79 1.436 3.054 3.505 3.753 6.149l-1.89.462c-.598-2.263-1.655-3.978-3.14-5.096-1.464-1.104-3.352-1.677-5.61-1.703-2.954.02-5.19.975-6.649 2.84C4.148 6.418 3.457 8.836 3.45 12.06c.008 3.227.698 5.647 2.052 7.197 1.458 1.67 3.694 2.527 6.647 2.548 2.308-.008 4.196-.548 5.616-1.606 1.425-1.062 2.37-2.557 2.806-4.444l1.872.518c-.524 2.282-1.693 4.117-3.474 5.455-1.78 1.338-4.093 2.028-6.876 2.05-.009 0-.012 0-.012 0l.105-.005zm4.892-7.812c-.243 1.166-.742 2.087-1.489 2.74-.756.662-1.74 1.009-2.934 1.032-1.57-.03-2.755-.527-3.525-1.478-.742-.917-1.117-2.193-1.117-3.798 0-1.503.36-2.7 1.07-3.562.719-.87 1.762-1.315 3.104-1.327h.018c1.107.01 2.006.326 2.677.94.67.614 1.036 1.46 1.093 2.52.033.63-.048 1.177-.243 1.627-.19.44-.485.77-.878.983-.382.206-.842.305-1.368.295-.49-.01-.892-.16-1.195-.447-.288-.274-.444-.64-.464-1.088l-.001-.054c.016-.345.098-.64.245-.88.145-.237.352-.424.617-.555.26-.13.578-.195.946-.195.271 0 .533.038.78.113l.484-1.43a4.153 4.153 0 00-1.263-.184c-.698 0-1.315.147-1.836.437-.525.293-.936.707-1.221 1.233-.29.534-.434 1.152-.434 1.839 0 .787.181 1.448.538 1.966.358.519.848.906 1.46 1.153a4.87 4.87 0 001.962.376c.817-.012 1.538-.196 2.143-.549.605-.352 1.077-.85 1.404-1.48.327-.63.496-1.362.504-2.178-.06-1.478-.555-2.657-1.475-3.507-.92-.85-2.167-1.285-3.713-1.296h-.027c-1.788.015-3.17.596-4.107 1.73-.932 1.128-1.406 2.696-1.406 4.663 0 2.068.487 3.696 1.45 4.844 1 1.19 2.472 1.806 4.379 1.835 1.592-.024 2.908-.481 3.915-1.362 1.002-.875 1.655-2.082 1.944-3.59l-1.86-.413z"/>
                            </svg>
                        </a>
                        <a 
                            href="https://tiktok.com/@sitegradepro" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-magenta transition-colors"
                            data-testid="social-tiktok"
                            aria-label="TikTok"
                        >
                            {/* TikTok icon (custom SVG) */}
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                            </svg>
                        </a>
                        <a 
                            href="https://discord.gg/GAfmKjNk" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-magenta transition-colors"
                            data-testid="social-discord"
                            aria-label="Discord"
                        >
                            {/* Discord icon (custom SVG) */}
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                            </svg>
                        </a>
                        <a 
                            href="https://www.reddit.com/r/SiteGradePro/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-magenta transition-colors"
                            data-testid="social-reddit"
                            aria-label="Reddit"
                        >
                            {/* Reddit icon (custom SVG) */}
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z"/>
                            </svg>
                        </a>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm flex-wrap justify-center md:justify-end">
                        <button 
                            onClick={() => setShowPrivacyPolicy(true)} 
                            className="text-gray-400 hover:text-magenta transition-colors"
                        >
                            Privacy Policy
                        </button>
                        <span className="text-gray-600">|</span>
                        <button 
                            onClick={() => setShowTerms(true)} 
                            className="text-gray-400 hover:text-magenta transition-colors"
                        >
                            Terms & Conditions
                        </button>
                        <span className="text-gray-600">|</span>
                        <a href="mailto:hello@sitegradepro.com" className="text-gray-400 hover:text-magenta transition-colors">
                            Contact Us
                        </a>
                        <span className="text-gray-600">|</span>
                        <span className="text-gray-500">Powered by Kamelda's Designs</span>
                    </div>
                </div>
            </footer>

            {/* Privacy Policy Modal */}
            {showPrivacyPolicy && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div 
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={() => setShowPrivacyPolicy(false)}
                    />
                    <Card className="relative z-10 glass-card border-white/10 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
                        <CardHeader className="sticky top-0 bg-obsidian/95 backdrop-blur-sm border-b border-white/10 z-10">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-2xl font-heading text-white">Privacy Policy</CardTitle>
                                <Button 
                                    variant="ghost" 
                                    onClick={() => setShowPrivacyPolicy(false)}
                                    className="text-gray-400 hover:text-white"
                                >
                                    <X className="h-5 w-5" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6 prose prose-invert max-w-none">
                            <p className="text-gray-400 text-sm mb-6">Last updated: February 21, 2026</p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">1. Introduction</h3>
                            <p className="text-gray-300 mb-4">
                                SiteGradePro ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website analysis service.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">2. Information We Collect</h3>
                            <p className="text-gray-300 mb-2">We collect information you provide directly to us, including:</p>
                            <ul className="text-gray-300 mb-4 list-disc pl-6 space-y-1">
                                <li>Website URLs you submit for analysis</li>
                                <li>Email address (when provided for reports)</li>
                                <li>Account information (name, email) when you sign up</li>
                                <li>Payment information (processed securely by Stripe)</li>
                                <li>Contact form submissions</li>
                            </ul>
                            
                            <h3 className="text-white font-heading text-lg mb-3">3. How We Use Your Information</h3>
                            <p className="text-gray-300 mb-2">We use the information we collect to:</p>
                            <ul className="text-gray-300 mb-4 list-disc pl-6 space-y-1">
                                <li>Provide website analysis and scoring services</li>
                                <li>Send you analysis reports and notifications</li>
                                <li>Process payments and manage subscriptions</li>
                                <li>Improve our services and develop new features</li>
                                <li>Respond to your inquiries and provide customer support</li>
                                <li>Send marketing communications (with your consent)</li>
                            </ul>
                            
                            <h3 className="text-white font-heading text-lg mb-3">4. Information Sharing</h3>
                            <p className="text-gray-300 mb-4">
                                We do not sell your personal information. We may share your information with third-party service providers who assist us in operating our service (e.g., Stripe for payments, email service providers). These providers are bound by confidentiality obligations.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">5. Data Security</h3>
                            <p className="text-gray-300 mb-4">
                                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">6. Your Rights</h3>
                            <p className="text-gray-300 mb-2">You have the right to:</p>
                            <ul className="text-gray-300 mb-4 list-disc pl-6 space-y-1">
                                <li>Access and receive a copy of your personal data</li>
                                <li>Request correction of inaccurate data</li>
                                <li>Request deletion of your data</li>
                                <li>Opt-out of marketing communications</li>
                                <li>Cancel your subscription at any time</li>
                            </ul>
                            
                            <h3 className="text-white font-heading text-lg mb-3">7. Cookies</h3>
                            <p className="text-gray-300 mb-4">
                                We use cookies and similar technologies to maintain your session, remember your preferences, and analyze how our service is used. You can control cookies through your browser settings.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">8. Contact Us</h3>
                            <p className="text-gray-300 mb-4">
                                If you have questions about this Privacy Policy or our data practices, please contact us at:<br />
                                <a href="mailto:hello@sitegradepro.com" className="text-magenta hover:underline">hello@sitegradepro.com</a>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Terms & Conditions Modal */}
            {showTerms && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
                    <div 
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={() => setShowTerms(false)}
                    />
                    <Card className="relative z-10 glass-card border-white/10 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
                        <CardHeader className="sticky top-0 bg-obsidian/95 backdrop-blur-sm border-b border-white/10 z-10">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-2xl font-heading text-white">Terms & Conditions</CardTitle>
                                <Button 
                                    variant="ghost" 
                                    onClick={() => setShowTerms(false)}
                                    className="text-gray-400 hover:text-white"
                                >
                                    <X className="h-5 w-5" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6 prose prose-invert max-w-none">
                            <p className="text-gray-400 text-sm mb-6">Last updated: February 21, 2026</p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">1. Acceptance of Terms</h3>
                            <p className="text-gray-300 mb-4">
                                By accessing or using SiteGradePro ("Service"), you agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use our Service.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">2. Description of Service</h3>
                            <p className="text-gray-300 mb-4">
                                SiteGradePro provides AI-powered website analysis and scoring services. We analyze websites for performance, mobile-friendliness, SEO, and conversion optimization, providing actionable recommendations.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">3. User Accounts</h3>
                            <p className="text-gray-300 mb-4">
                                To access certain features, you may need to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must provide accurate information and promptly update any changes.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">4. Subscription Plans & Payments</h3>
                            <ul className="text-gray-300 mb-4 list-disc pl-6 space-y-1">
                                <li>Subscription fees are billed monthly in advance</li>
                                <li>All payments are processed securely through Stripe</li>
                                <li>Prices are subject to change with 30 days notice</li>
                                <li>Refunds are provided at our discretion</li>
                                <li>You may cancel your subscription at any time</li>
                            </ul>
                            
                            <h3 className="text-white font-heading text-lg mb-3">5. Acceptable Use</h3>
                            <p className="text-gray-300 mb-2">You agree not to:</p>
                            <ul className="text-gray-300 mb-4 list-disc pl-6 space-y-1">
                                <li>Use the Service for any unlawful purpose</li>
                                <li>Attempt to gain unauthorized access to our systems</li>
                                <li>Interfere with or disrupt the Service</li>
                                <li>Submit malicious URLs or content</li>
                                <li>Resell or redistribute the Service without authorization</li>
                                <li>Use automated tools to access the Service excessively</li>
                            </ul>
                            
                            <h3 className="text-white font-heading text-lg mb-3">6. Intellectual Property</h3>
                            <p className="text-gray-300 mb-4">
                                All content, features, and functionality of the Service are owned by SiteGradePro and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute our content without permission.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">7. Disclaimer of Warranties</h3>
                            <p className="text-gray-300 mb-4">
                                The Service is provided "as is" without warranties of any kind. We do not guarantee that our analysis will be error-free or that following our recommendations will achieve specific results. Website performance depends on many factors beyond our control.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">8. Limitation of Liability</h3>
                            <p className="text-gray-300 mb-4">
                                To the maximum extent permitted by law, SiteGradePro shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability shall not exceed the amount you paid us in the past 12 months.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">9. Changes to Terms</h3>
                            <p className="text-gray-300 mb-4">
                                We may update these Terms & Conditions from time to time. We will notify you of significant changes by posting the new terms on our website. Your continued use of the Service after changes constitutes acceptance of the new terms.
                            </p>
                            
                            <h3 className="text-white font-heading text-lg mb-3">10. Contact</h3>
                            <p className="text-gray-300 mb-4">
                                For questions about these Terms & Conditions, please contact us at:<br />
                                <a href="mailto:hello@sitegradepro.com" className="text-magenta hover:underline">hello@sitegradepro.com</a>
                            </p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Auth Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={() => {
                            setShowAuthModal(false);
                            setAuthError('');
                            setAuthForm({ name: '', email: '', password: '' });
                        }}
                    />
                    <Card className="relative z-10 glass-card border-white/10 w-full max-w-md animate-scale-in" data-testid="auth-modal">
                        <CardHeader>
                            <CardTitle className="text-2xl font-heading text-white flex items-center gap-3">
                                <img src={LOGO_URL} alt="" className="h-8 object-contain" />
                                {authMode === 'login' ? 'Sign In' : 'Create Account'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {/* Auth Mode Toggle */}
                            <div className="flex gap-2 mb-6">
                                <button
                                    onClick={() => { setAuthMode('login'); setAuthError(''); }}
                                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                        authMode === 'login' 
                                            ? 'bg-magenta text-white' 
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                                    }`}
                                    data-testid="auth-login-tab"
                                >
                                    Sign In
                                </button>
                                <button
                                    onClick={() => { setAuthMode('register'); setAuthError(''); }}
                                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                        authMode === 'register' 
                                            ? 'bg-magenta text-white' 
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                                    }`}
                                    data-testid="auth-register-tab"
                                >
                                    Register
                                </button>
                            </div>

                            {/* Error Message */}
                            {authError && (
                                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm" data-testid="auth-error">
                                    {authError}
                                </div>
                            )}

                            {/* Auth Form */}
                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                setAuthError('');
                                setIsAuthSubmitting(true);
                                try {
                                    if (authMode === 'register') {
                                        if (!authForm.name.trim()) {
                                            setAuthError('Please enter your name');
                                            return;
                                        }
                                        await registerWithEmail(authForm.name, authForm.email, authForm.password);
                                        toast.success('Account created successfully!');
                                    } else {
                                        await loginWithEmail(authForm.email, authForm.password);
                                        toast.success('Signed in successfully!');
                                    }
                                    setShowAuthModal(false);
                                    setAuthForm({ name: '', email: '', password: '' });
                                } catch (error) {
                                    let message = 'Authentication failed. Please try again.';
                                    const detail = error.response?.data?.detail;
                                    if (typeof detail === 'string') {
                                        message = detail;
                                    } else if (Array.isArray(detail)) {
                                        message = detail.map(e => e.msg || e).join(', ');
                                    } else if (detail && typeof detail === 'object') {
                                        message = detail.msg || JSON.stringify(detail);
                                    }
                                    setAuthError(message);
                                } finally {
                                    setIsAuthSubmitting(false);
                                }
                            }} className="space-y-4">
                                {authMode === 'register' && (
                                    <div>
                                        <label className="text-sm text-gray-400 mb-1 block">Name</label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                            <Input
                                                type="text"
                                                placeholder="Your name"
                                                value={authForm.name}
                                                onChange={(e) => setAuthForm(prev => ({ ...prev, name: e.target.value }))}
                                                className="form-input pl-10 bg-black/50 border-gray-800 text-white"
                                                data-testid="auth-name-input"
                                                required
                                            />
                                        </div>
                                    </div>
                                )}
                                
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Email</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                        <Input
                                            type="email"
                                            placeholder="your@email.com"
                                            value={authForm.email}
                                            onChange={(e) => setAuthForm(prev => ({ ...prev, email: e.target.value }))}
                                            className="form-input pl-10 bg-black/50 border-gray-800 text-white"
                                            data-testid="auth-email-input"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                        <Input
                                            type="password"
                                            placeholder="••••••••"
                                            value={authForm.password}
                                            onChange={(e) => setAuthForm(prev => ({ ...prev, password: e.target.value }))}
                                            className="form-input pl-10 bg-black/50 border-gray-800 text-white"
                                            data-testid="auth-password-input"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                    {authMode === 'register' && (
                                        <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                                    )}
                                </div>
                                
                                <Button
                                    type="submit"
                                    disabled={isAuthSubmitting}
                                    className="w-full bg-magenta hover:bg-magenta-dark text-white font-bold"
                                    data-testid="auth-submit-btn"
                                >
                                    {isAuthSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {authMode === 'login' ? 'Signing in...' : 'Creating account...'}
                                        </>
                                    ) : (
                                        authMode === 'login' ? 'Sign In' : 'Create Account'
                                    )}
                                </Button>
                            </form>

                            {/* Divider */}
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-3 bg-[#0a0a0a] text-gray-500">Or continue with</span>
                                </div>
                            </div>

                            {/* Google Sign In */}
                            <Button
                                type="button"
                                onClick={() => {
                                    setShowAuthModal(false);
                                    login();
                                }}
                                variant="outline"
                                className="w-full border-gray-700 text-gray-300 hover:border-white hover:text-white"
                                data-testid="google-signin-btn"
                            >
                                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                Continue with Google
                            </Button>

                            {/* Close button */}
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => {
                                    setShowAuthModal(false);
                                    setAuthError('');
                                    setAuthForm({ name: '', email: '', password: '' });
                                }}
                                className="w-full mt-3 text-gray-400 hover:text-white"
                                data-testid="auth-close-btn"
                            >
                                Cancel
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Consultation Modal */}
            {showConsultation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={() => setShowConsultation(false)}
                    />
                    <Card className="relative z-10 glass-card border-white/10 w-full max-w-lg animate-scale-in" data-testid="consultation-modal">
                        <CardHeader>
                            <CardTitle className="text-2xl font-heading text-white flex items-center gap-3">
                                <img src={LOGO_URL} alt="" className="h-8 object-contain" />
                                Get Expert Help
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={submitConsultation} className="space-y-4">
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Name *</label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                        <Input
                                            type="text"
                                            placeholder="Your name"
                                            value={consultForm.name}
                                            onChange={(e) => setConsultForm(prev => ({ ...prev, name: e.target.value }))}
                                            className="form-input pl-10 bg-black/50 border-gray-800 text-white"
                                            data-testid="consult-name-input"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                        <Input
                                            type="email"
                                            placeholder="your@email.com"
                                            value={consultForm.email}
                                            onChange={(e) => setConsultForm(prev => ({ ...prev, email: e.target.value }))}
                                            className="form-input pl-10 bg-black/50 border-gray-800 text-white"
                                            data-testid="consult-email-input"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Phone</label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                        <Input
                                            type="tel"
                                            placeholder="(555) 123-4567"
                                            value={consultForm.phone}
                                            onChange={(e) => setConsultForm(prev => ({ ...prev, phone: e.target.value }))}
                                            className="form-input pl-10 bg-black/50 border-gray-800 text-white"
                                            data-testid="consult-phone-input"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Website URL</label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                        <Input
                                            type="text"
                                            placeholder="yourwebsite.com"
                                            value={consultForm.website || analysisResult?.url || ""}
                                            onChange={(e) => setConsultForm(prev => ({ ...prev, website: e.target.value }))}
                                            className="form-input pl-10 bg-black/50 border-gray-800 text-white"
                                            data-testid="consult-website-input"
                                        />
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-sm text-gray-400 mb-1 block">Message</label>
                                    <div className="relative">
                                        <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                                        <Textarea
                                            placeholder="Tell us about your project..."
                                            value={consultForm.message}
                                            onChange={(e) => setConsultForm(prev => ({ ...prev, message: e.target.value }))}
                                            className="form-input pl-10 bg-black/50 border-gray-800 text-white min-h-[100px]"
                                            data-testid="consult-message-input"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowConsultation(false)}
                                        className="flex-1 border-gray-700 text-gray-300 hover:border-white hover:text-white"
                                        data-testid="cancel-consult-btn"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={isSubmittingConsult}
                                        className="flex-1 bg-magenta hover:bg-magenta-dark text-white font-bold"
                                        data-testid="submit-consult-btn"
                                    >
                                        {isSubmittingConsult ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            "Submit"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}
            
            {/* Upgrade Modal - shown when scan limit reached */}
            {showUpgradeModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="upgrade-modal">
                    <div 
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                        onClick={() => setShowUpgradeModal(false)}
                    />
                    <Card className="relative z-10 glass-card border-amber-500/30 w-full max-w-md animate-scale-in">
                        <CardContent className="p-8 text-center">
                            <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                                <AlertTriangle className="h-8 w-8 text-amber-400" />
                            </div>
                            <h3 className="text-2xl font-heading font-bold text-white mb-2">
                                Scan Limit Reached
                            </h3>
                            <p className="text-gray-400 mb-6">
                                You've used all your scans for this month. Upgrade your plan to continue analyzing websites.
                            </p>
                            
                            <div className="space-y-3">
                                {subscription?.tier === "free" && (
                                    <Button
                                        onClick={() => {
                                            setShowUpgradeModal(false);
                                            handleCheckout('starter');
                                        }}
                                        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
                                        data-testid="upgrade-starter-btn"
                                    >
                                        <Zap className="mr-2 h-4 w-4" />
                                        Get Starter - $19/mo (10 scans)
                                    </Button>
                                )}
                                {(subscription?.tier === "free" || subscription?.tier === "starter") && (
                                    <Button
                                        onClick={() => {
                                            setShowUpgradeModal(false);
                                            handleCheckout('pro');
                                        }}
                                        className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold"
                                        data-testid="upgrade-pro-btn"
                                    >
                                        <Sparkles className="mr-2 h-4 w-4" />
                                        {subscription?.tier === "starter" ? "Upgrade to " : "Get "}Pro - $39/mo (Unlimited)
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    onClick={() => setShowUpgradeModal(false)}
                                    className="w-full text-gray-400 hover:text-white"
                                >
                                    Maybe Later
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

// App wrapper with AuthProvider
function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
